/**
 * MatchCustomerModal. Surfaces ranked Sage customer candidates for an order
 * that doesn't yet have a Sage CustomerNo. Two stacked sections:
 *
 *   - Suggested matches: top candidates from /match-candidates, scored by
 *     existing mapping (1.0) or normalized-set similarity against the
 *     Faire partner name. Each candidate shows the score as a percentage.
 *   - Search: free-text fall-back via /sales/faire-sage/customers/search,
 *     pre-filtered by ship-to state when the envelope has one.
 *
 * Confirming a pick POSTs /commerce/orders/{id}/match, which writes the
 * faire_retailer_to_sage_customer mapping, advances the Faire state row to
 * 'queued', and updates commerce_orders.partner_id/partner_name/status. The
 * UI re-fetches the orders list on success.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';

import {
  formatOrderNumber,
  useApplyMatch,
  useMatchCandidates,
  useSageCustomerSearch,
  type ChannelAddress,
  type MatchCandidate,
  type SageCustomerHit,
} from '@/lib/useOrders';

type Props = {
  orderId: string;
  sourceOrderId?: string | null;
  onClose: () => void;
  onMatched?: (sageCustomerNo: string, customerName: string | null) => void;
};

export function MatchCustomerModal({
  orderId,
  sourceOrderId,
  onClose,
  onMatched,
}: Props) {
  const candidatesQ = useMatchCandidates(orderId);
  const apply = useApplyMatch();
  const [chosen, setChosen] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const autoSelectedRef = useRef(false);
  const stateFilter = candidatesQ.data?.ship_to_state ?? null;
  const searchQ = useSageCustomerSearch(search, stateFilter, showSearch && search.length >= 2);

  useEffect(() => {
    if (!candidatesQ.data || autoSelectedRef.current) return;
    autoSelectedRef.current = true;
    const confident = candidatesQ.data.candidates.find(
      (c) => c.source === 'existing_mapping' || c.score >= 0.8,
    );
    if (confident) {
      setChosen(confident.sage_customer_no);
    } else {
      setShowSearch(true);
    }
  }, [candidatesQ.data]);

  const onConfirm = () => {
    if (!chosen) return;
    apply.mutate(
      { orderId, sage_customer_no: chosen },
      {
        onSuccess: (res) => {
          onMatched?.(res.sage_customer_no, res.customer_name);
          onClose();
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative w-full max-w-2xl rounded-lg border border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-5 pt-5 pb-3 border-b border-amp-line dark:border-amp-dark-line">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            Match Sage customer
          </p>
          <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">
            {sourceOrderId ? formatOrderNumber(sourceOrderId) : orderId.slice(0, 8)}
          </h2>
          {candidatesQ.data?.partner_name && (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Partner from Faire: <span className="text-amp-ink dark:text-amp-dark-ink">{candidatesQ.data.partner_name}</span>
              {candidatesQ.data.ship_to_state ? ` (${candidatesQ.data.ship_to_state})` : ''}
            </p>
          )}
        </div>

        <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
          <FaireAddressCards
            billTo={candidatesQ.data?.bill_to ?? null}
            shipTo={candidatesQ.data?.ship_to ?? null}
          />

          <section>
            <h3 className="text-xs uppercase tracking-wide font-medium text-amp-olive dark:text-amp-olive-dark mb-2">
              Suggested matches
            </h3>
            {candidatesQ.isLoading ? (
              <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
            ) : candidatesQ.error ? (
              <p className="text-sm text-red-700 dark:text-red-300">{candidatesQ.error.message}</p>
            ) : (candidatesQ.data?.candidates.length ?? 0) === 0 ? (
              <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                No automatic matches. Use search below.
              </p>
            ) : (
              <ul className="space-y-1">
                {candidatesQ.data!.candidates.map((c) => (
                  <CandidateRow
                    key={c.sage_customer_no}
                    cand={c}
                    chosen={chosen === c.sage_customer_no}
                    onChoose={() => setChosen(c.sage_customer_no)}
                  />
                ))}
              </ul>
            )}
          </section>

          {!showSearch ? (
            <button
              type="button"
              onClick={() => setShowSearch(true)}
              className="text-xs text-amp-olive dark:text-amp-olive-dark underline underline-offset-2 hover:opacity-80"
            >
              Search for a different customer
            </button>
          ) : (
            <section>
              <h3 className="text-xs uppercase tracking-wide font-medium text-amp-olive dark:text-amp-olive-dark mb-2">
                Search Sage AR
              </h3>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={stateFilter ? `Search customers in ${stateFilter}...` : 'Search by customer name...'}
                className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink mb-2"
              />
              {searchQ.isLoading ? (
                <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Searching...</p>
              ) : searchQ.error ? (
                <p className="text-sm text-red-700 dark:text-red-300">{searchQ.error.message}</p>
              ) : search.length < 2 ? (
                <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Type at least 2 characters.
                </p>
              ) : (searchQ.data?.customers.length ?? 0) === 0 ? (
                <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No matches.
                </p>
              ) : (
                <ul className="space-y-1">
                  {searchQ.data!.customers.map((c) => (
                    <SearchRow
                      key={c.CustomerNo}
                      cust={c}
                      chosen={chosen === c.CustomerNo}
                      onChoose={() => setChosen(c.CustomerNo)}
                    />
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>

        <div className="px-5 py-3 border-t border-amp-line dark:border-amp-dark-line flex items-center justify-between">
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {chosen ? `Selected: ${chosen}` : 'Pick a customer to confirm.'}
            {apply.error && (
              <span className="ml-2 text-red-700 dark:text-red-300">
                {apply.error.message}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!chosen || apply.isPending}
              className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {apply.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              Match
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CandidateRow({
  cand,
  chosen,
  onChoose,
}: {
  cand: MatchCandidate;
  chosen: boolean;
  onChoose: () => void;
}) {
  const pct = Math.round(Math.max(0, Math.min(1, cand.score)) * 100);
  return (
    <li>
      <label
        className={`flex items-start gap-3 rounded border px-3 py-2 cursor-pointer ${
          chosen
            ? 'border-amp-olive bg-amp-cream-warm dark:border-amp-olive-dark dark:bg-amp-dark-surface-warm'
            : 'border-amp-line hover:bg-amp-cream-warm dark:border-amp-dark-line dark:hover:bg-amp-dark-surface-warm'
        }`}
      >
        <input
          type="radio"
          name="candidate"
          checked={chosen}
          onChange={onChoose}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-amp-ink dark:text-amp-dark-ink truncate">
              {cand.customer_name}
            </span>
            <ScoreBadge pct={pct} source={cand.source} />
          </div>
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <span className="font-mono">{cand.sage_customer_no}</span>
            {formatCityStateZip(cand.city, cand.state, cand.zip) && (
              <span className="ml-2">
                {formatCityStateZip(cand.city, cand.state, cand.zip)}
              </span>
            )}
            {cand.address_line_1 && (
              <span className="ml-2">{cand.address_line_1}</span>
            )}
          </div>
          {cand.matched_ship_to && (
            <div className="mt-1 pt-1 border-t border-amp-line dark:border-amp-dark-line text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
              <span className="text-[12px] uppercase tracking-wide mr-1">Matched ship-to:</span>
              {cand.matched_ship_to.company_name || cand.matched_ship_to.name}
              {cand.matched_ship_to.line1 && <span className="ml-1">{cand.matched_ship_to.line1}</span>}
              {formatCityStateZip(cand.matched_ship_to.city, cand.matched_ship_to.state, cand.matched_ship_to.zip) && (
                <span className="ml-1">{formatCityStateZip(cand.matched_ship_to.city, cand.matched_ship_to.state, cand.matched_ship_to.zip)}</span>
              )}
            </div>
          )}
        </div>
      </label>
    </li>
  );
}

function SearchRow({
  cust,
  chosen,
  onChoose,
}: {
  cust: SageCustomerHit;
  chosen: boolean;
  onChoose: () => void;
}) {
  return (
    <li>
      <label
        className={`flex items-start gap-3 rounded border px-3 py-2 cursor-pointer ${
          chosen
            ? 'border-amp-olive bg-amp-cream-warm dark:border-amp-olive-dark dark:bg-amp-dark-surface-warm'
            : 'border-amp-line hover:bg-amp-cream-warm dark:border-amp-dark-line dark:hover:bg-amp-dark-surface-warm'
        }`}
      >
        <input
          type="radio"
          name="candidate"
          checked={chosen}
          onChange={onChoose}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink truncate">
            {cust.CustomerName}
          </div>
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <span className="font-mono">{cust.CustomerNo}</span>
            {formatCityStateZip(cust.City, cust.State, cust.ZipCode) && (
              <span className="ml-2">
                {formatCityStateZip(cust.City, cust.State, cust.ZipCode)}
              </span>
            )}
            {cust.AddressLine1 && (
              <span className="ml-2">{cust.AddressLine1}</span>
            )}
          </div>
        </div>
      </label>
    </li>
  );
}

// Compact rendering of "City, ST 12345" with graceful skips when any segment
// is missing. Returns an empty string only when every input is empty.
function formatCityStateZip(
  city: string | null,
  state: string | null,
  zip: string | null,
): string {
  const left = [city, state].filter((s): s is string => !!s && !!s.trim()).join(', ');
  const z = zip && zip.trim() ? zip.trim() : '';
  if (left && z) return `${left} ${z}`;
  return left || z;
}

// True when two address blocks point at the same physical address (line1 +
// city + state + zip match, case-insensitive). Used to skip rendering the
// ship-to card when the order's ship-to is identical to its bill-to.
function addressesEqual(a: ChannelAddress, b: ChannelAddress): boolean {
  const norm = (s: string | null) => (s ?? '').trim().toUpperCase();
  return (
    norm(a.line1) === norm(b.line1) &&
    norm(a.line2) === norm(b.line2) &&
    norm(a.city) === norm(b.city) &&
    norm(a.state) === norm(b.state) &&
    norm(a.zip) === norm(b.zip)
  );
}

function FaireAddressCards({
  billTo,
  shipTo,
}: {
  billTo: ChannelAddress | null;
  shipTo: ChannelAddress | null;
}) {
  if (!billTo && !shipTo) return null;
  const billDiffers =
    billTo !== null && (shipTo === null || !addressesEqual(billTo, shipTo));
  return (
    <section>
      <h3 className="text-xs uppercase tracking-wide font-medium text-amp-olive dark:text-amp-olive-dark mb-2">
        From Faire
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {shipTo && <AddressCard title="Ship to" addr={shipTo} />}
        {billDiffers && billTo && <AddressCard title="Bill to" addr={billTo} />}
        {billTo && !billDiffers && shipTo && (
          <div className="rounded border border-dashed border-amp-line dark:border-amp-dark-line px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Bill to is the same as ship to.
          </div>
        )}
      </div>
    </section>
  );
}

function AddressCard({ title, addr }: { title: string; addr: ChannelAddress }) {
  const cityStateZip = formatCityStateZip(addr.city, addr.state, addr.zip);
  // Prefer the freight-bill company name, then the contact name; fall back to
  // a parenthetical placeholder when both are missing.
  const headline = addr.company_name || addr.name || '(name not provided)';
  const sub = addr.company_name && addr.name && addr.company_name !== addr.name
    ? addr.name
    : null;
  return (
    <div className="rounded border border-amp-line dark:border-amp-dark-line px-3 py-2 bg-amp-cream-warm/40 dark:bg-amp-dark-surface-warm/40">
      <FilterLabel as="div" className="!mb-0.5">
        {title}
      </FilterLabel>
      <div className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink truncate">
        {headline}
      </div>
      {sub && (
        <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted truncate">
          {sub}
        </div>
      )}
      <div className="text-xs text-amp-ink dark:text-amp-dark-ink mt-1 space-y-0.5">
        {addr.line1 && <div className="truncate">{addr.line1}</div>}
        {addr.line2 && <div className="truncate">{addr.line2}</div>}
        {cityStateZip && <div className="truncate">{cityStateZip}</div>}
        {addr.country && addr.country.toUpperCase() !== 'USA' && (
          <div className="truncate">{addr.country}</div>
        )}
      </div>
      {addr.phone && (
        <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1">
          {addr.phone}
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ pct, source }: { pct: number; source: MatchCandidate['source'] }) {
  const tone =
    source === 'existing_mapping' || pct >= 80
      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
      : pct >= 50
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
        : 'bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink';
  return (
    <Pill tone={tone}>
      {source === 'existing_mapping' ? `Existing map · ${pct}%` : `${pct}%`}
    </Pill>
  );
}
